import type {
  ActiveMention,
  MentionOption,
  MentionPopupPosition,
  MentionTextPart
} from "./types";

import classes from "./styles.module.css";

export const DEFAULT_POPUP_HEIGHT = 240;

const POPUP_GAP = 6;
const POPUP_MARGIN = 8;
const POPUP_WIDTH = 280;
const MENTION_QUERY_PATTERN = /(?:^|\s)@([\p{L}\p{N}_.-]*)$/u;
const MASK_TOKEN_PATTERN = /__label__|__id__/g;
const BLOCK_ELEMENTS = new Set(["DIV", "LI", "P"]);

export const readVisibleText = (element: HTMLElement) => {
  const text = element.innerText.replace(/\r\n?/g, "\n");

  // Browsers can leave a single <br> in an empty contentEditable element.
  return text === "\n" && !element.textContent ? "" : text;
};

export const getMentionValue = (mention: MentionOption, mask?: string) => {
  if (!mask) {
    return mention.id;
  }

  return mask.replace(MASK_TOKEN_PATTERN, token =>
    token === "__label__" ? mention.label : mention.id
  );
};

export const createMentionElement = (mention: MentionOption, mask?: string) => {
  const element = document.createElement("span");

  element.className = classes.mention;
  element.contentEditable = "false";
  element.dataset.mentionValue = getMentionValue(mention, mask);
  element.textContent = mention.label;

  return element;
};

export const readValue = (element: HTMLElement) => {
  let value = "";

  const appendNodeValue = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      value += node.textContent || "";
      return;
    }

    if (!(node instanceof HTMLElement)) {
      return;
    }

    const mentionValue = node.dataset.mentionValue ?? node.dataset.mentionId;

    if (mentionValue !== undefined) {
      value += mentionValue;
      return;
    }

    if (node.tagName === "BR") {
      value += "\n";
      return;
    }

    const isBlock = BLOCK_ELEMENTS.has(node.tagName);

    if (isBlock && value && !value.endsWith("\n")) {
      value += "\n";
    }

    const isEmptyBlock =
      isBlock &&
      node.childNodes.length === 1 &&
      (node.firstChild as HTMLElement | null)?.tagName === "BR";

    if (!isEmptyBlock) {
      node.childNodes.forEach(appendNodeValue);
    }
  };

  element.childNodes.forEach(appendNodeValue);

  return value === "\n" && !element.textContent
    ? ""
    : value.replace(/\r\n?/g, "\n");
};

export const parseMentionText = (
  value: string,
  mentions: MentionOption[],
  mask?: string
) => {
  const availableMentions = mentions
    .map(mention => ({
      mention,
      value: getMentionValue(mention, mask)
    }))
    .filter(mention => mention.value)
    .sort((first, second) => second.value.length - first.value.length);
  const parts: MentionTextPart[] = [];
  let currentIndex = 0;

  while (currentIndex < value.length) {
    let nextMention: (typeof availableMentions)[number] | null = null;
    let nextMentionIndex = -1;

    for (const mention of availableMentions) {
      const index = value.indexOf(mention.value, currentIndex);

      if (index >= 0 && (nextMentionIndex === -1 || index < nextMentionIndex)) {
        nextMention = mention;
        nextMentionIndex = index;
      }
    }

    if (!nextMention || nextMentionIndex < 0) {
      parts.push({ type: "text", value: value.slice(currentIndex) });
      break;
    }

    if (nextMentionIndex > currentIndex) {
      parts.push({
        type: "text",
        value: value.slice(currentIndex, nextMentionIndex)
      });
    }

    parts.push({
      type: "mention",
      value: nextMention.value,
      mention: nextMention.mention
    });
    currentIndex = nextMentionIndex + nextMention.value.length;
  }

  return parts;
};

export const createValueFragment = (
  value: string,
  mentions: MentionOption[],
  mask?: string
) => {
  const fragment = document.createDocumentFragment();

  parseMentionText(value, mentions, mask).forEach(part => {
    fragment.append(
      part.type === "mention"
        ? createMentionElement(part.mention, mask)
        : document.createTextNode(part.value)
    );
  });

  return fragment;
};

export const renderValue = (
  element: HTMLElement,
  value: string,
  mentions: MentionOption[],
  mask?: string
) => {
  element.replaceChildren(createValueFragment(value, mentions, mask));
};

export const getCaretOffset = (element: HTMLElement) => {
  const selection = window.getSelection();

  if (!selection?.rangeCount) {
    return null;
  }

  const selectedRange = selection.getRangeAt(0);

  if (!element.contains(selectedRange.endContainer)) {
    return null;
  }

  const range = selectedRange.cloneRange();
  range.selectNodeContents(element);
  range.setEnd(selectedRange.endContainer, selectedRange.endOffset);

  return range.toString().length;
};

export const getRangeForOffsets = (
  element: HTMLElement,
  startOffset: number,
  endOffset: number
) => {
  const range = document.createRange();
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let currentOffset = 0;
  let hasStart = false;
  let node = walker.nextNode();

  while (node) {
    const nodeLength = node.textContent?.length || 0;
    const nextOffset = currentOffset + nodeLength;

    if (!hasStart && startOffset <= nextOffset) {
      range.setStart(node, Math.max(0, startOffset - currentOffset));
      hasStart = true;
    }

    if (hasStart && endOffset <= nextOffset) {
      range.setEnd(node, Math.max(0, endOffset - currentOffset));
      return range;
    }

    currentOffset = nextOffset;
    node = walker.nextNode();
  }

  return null;
};

export const getActiveMention = (
  text: string,
  caretOffset: number
): ActiveMention | null => {
  const textBeforeCaret = text.slice(0, caretOffset);
  const match = textBeforeCaret.match(MENTION_QUERY_PATTERN);

  if (!match) {
    return null;
  }

  const start = textBeforeCaret.lastIndexOf("@");

  return {
    query: match[1],
    start,
    end: caretOffset
  };
};

export const getPopupPosition = (
  element: HTMLElement,
  popupHeight: number
): MentionPopupPosition => {
  const selection = window.getSelection();
  const inputRect = element.getBoundingClientRect();
  const selectedRange = selection?.rangeCount ? selection.getRangeAt(0) : null;
  const rangeRect =
    selectedRange && element.contains(selectedRange.endContainer)
      ? selectedRange.getBoundingClientRect()
      : null;
  const caretRect =
    rangeRect && (rangeRect.height || rangeRect.width) ? rangeRect : inputRect;
  const availableWidth = Math.min(
    POPUP_WIDTH,
    window.innerWidth - POPUP_MARGIN * 2
  );
  const left = Math.min(
    Math.max(POPUP_MARGIN, caretRect.left),
    window.innerWidth - availableWidth - POPUP_MARGIN
  );
  const availableSpaceAbove = caretRect.top - POPUP_GAP - POPUP_MARGIN;
  const availableSpaceBelow =
    window.innerHeight - caretRect.bottom - POPUP_GAP - POPUP_MARGIN;
  const shouldOpenAbove =
    availableSpaceBelow < popupHeight &&
    availableSpaceAbove > availableSpaceBelow;

  return {
    left,
    top: shouldOpenAbove
      ? Math.max(POPUP_MARGIN, caretRect.top - popupHeight - POPUP_GAP)
      : caretRect.bottom + POPUP_GAP
  };
};

export const findEnabledOption = (
  options: MentionOption[],
  currentIndex: number,
  direction: 1 | -1
) => {
  if (!options.length) {
    return -1;
  }

  for (let step = 1; step <= options.length; step += 1) {
    const index =
      (currentIndex + direction * step + options.length) % options.length;

    if (!options[index].disabled) {
      return index;
    }
  }

  return -1;
};
